import React from 'react'
import {useInView} from 'react-intersection-observer'

import {homepageActioned, HomepageActionedAction} from '@/analytics/segment'
import type {HomepageV4Query} from '@/graphql-types'
import {GetStartedSlice} from '@/slices/get-started'
import exhaustiveCheck from '@/utils/exhaustive-check'
import {isEmptyObject} from '@/utils/is-empty-object'

import {AnimationBlockSlice} from './animation-block'
import {FeatureBlockWithMarquee} from './feature-block-with-marquee'
import {FeaturePanelSlice} from './feature-panel'
import {ResourceSlider} from './resource-slider'
import {StickyCards} from './sticky-cards'
import {StickyNavSections} from './sticky-nav-sections'
import {UseCaseSlice} from './use-case'
import {VideoHero} from './video-hero'
import {CustomerLogosWithCta} from '../../slices/customer-logos-cta'

type SliceProps = {
    slice: SliceData
    trackVisibleSections: Props['trackVisibleSections']
}

const Slice = ({slice, trackVisibleSections}: SliceProps) => {
    const typename = slice.__typename
    const [sliceRef] = useInView({
        skip: !trackVisibleSections,
        threshold: 1,
        triggerOnce: true,
        onChange: (isVisible) =>
            isVisible &&
            homepageActioned({
                action: HomepageActionedAction.SectionViewed,
                section: getHomepageSectionName(typename),
            }),
    })

    switch (typename) {
        case 'PrismicHomepageV4DataBodyFeatureBlockWithMarquee':
            return <FeatureBlockWithMarquee ref={sliceRef} slice={slice} />
        case 'PrismicHomepageV4DataBodyVideoHero':
            return <VideoHero ref={sliceRef} slice={slice} />
        case 'PrismicHomepageV4DataBodyStickyNavSections':
            return <StickyNavSections ref={sliceRef} slice={slice} />
        case 'PrismicHomepageV4DataBodyGetStarted':
            return <GetStartedSlice ref={sliceRef} slice={slice} dataTestId="get-started" />
        case 'PrismicHomepageV4DataBodyResourceSlider':
            return <ResourceSlider ref={sliceRef} slice={slice} />
        case 'PrismicHomepageV4DataBodyAnimationBlock':
            return <AnimationBlockSlice ref={sliceRef} slice={slice} />
        case 'PrismicHomepageV4DataBodyFeaturePanel':
            return <FeaturePanelSlice ref={sliceRef} slice={slice} />
        case 'PrismicHomepageV4DataBodyStickyCards':
            return <StickyCards ref={sliceRef} slice={slice} />
        case 'PrismicHomepageV4DataBodyCustomerLogosPlusCta':
            return (
                <CustomerLogosWithCta
                    ref={sliceRef}
                    ctaText={slice.primary.cta_text}
                    ctaLink={slice.primary.cta_link}
                    title={slice.primary.title}
                    logos={slice.items.map(({logo}) => logo)}
                    isMarquee={slice.primary.marquee_mode}
                    backgroundColor={slice.primary.background_color}
                    stats={[
                        {
                            highlight: slice.primary.stat_1_highlight,
                            body: slice.primary.stat_1_body,
                        },
                        {
                            highlight: slice.primary.stat_2_highlight,
                            body: slice.primary.stat_2_body,
                        },
                        {
                            highlight: slice.primary.stat_3_highlight,
                            body: slice.primary.stat_3_body,
                        },
                    ]}
                />
            )
        case 'PrismicHomepageV4DataBodyUseCase':
            return <UseCaseSlice ref={sliceRef} slice={slice} />
        default:
            exhaustiveCheck(typename)
            throw new Error(`Unknown slice: ${JSON.stringify(slice)}`)
    }
}

type HomepageV4QueryData = HomepageV4Query['prismicHomepageV4']['data']

const getHomepageSectionName = (type: HomepageV4QueryData['body'][number]['__typename']) => {
    switch (type) {
        case 'PrismicHomepageV4DataBodyVideoHero':
            return 'Video Hero'
        case 'PrismicHomepageV4DataBodyAnimationBlock':
            return 'Animation Block'
        case 'PrismicHomepageV4DataBodyCustomerLogosPlusCta':
            return 'Customer Logos Plus Cta'
        case 'PrismicHomepageV4DataBodyFeatureBlockWithMarquee':
            return 'Content Feature block with Marquee'
        case 'PrismicHomepageV4DataBodyGetStarted':
            return 'Get Started'
        case 'PrismicHomepageV4DataBodyResourceSlider':
            return 'Resources Slider'
        case 'PrismicHomepageV4DataBodyStickyNavSections':
            return 'Sticky Nav Sections'
        case 'PrismicHomepageV4DataBodyFeaturePanel':
            return 'Feature Panel'
        case 'PrismicHomepageV4DataBodyUseCase':
            return 'Use Case'
        case 'PrismicHomepageV4DataBodyStickyCards':
            return 'Sticky Cards'
        default:
            exhaustiveCheck(type)
            return 'Unknown Homepage Section'
    }
}

type SliceData = HomepageV4QueryData['body'][number]

type Props = {
    slices: SliceData[]
    trackVisibleSections: HomepageV4QueryData['track_visible_sections']
}

export const SliceZone = ({slices, trackVisibleSections}: Props) => {
    return (
        <>
            {slices.map((slice, i) => {
                // New slices added in Prismic will show up as empty objects
                // until we explicitly query content for them
                if (isEmptyObject(slice)) {
                    return null
                }

                if (!slice.__typename) {
                    throw new Error(`Missing __typename in slice: ${JSON.stringify(slice)}`)
                }

                return (
                    <Slice
                        key={`${slice.__typename}-${i}`}
                        slice={slice}
                        trackVisibleSections={trackVisibleSections}
                    />
                )
            })}
        </>
    )
}
